import { useMutation } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/lib/base/buttons/button';
import { SlideoutMenu } from '@/lib/base/overlays/slideout-menu';
import { useSurfaceDismiss } from '@/lib/base/overlays/surface-dismiss-context';
import type { WebhookSecretCreatedData } from '@/lib/crm/components/webhooks/WebhookSecretCreatedModal';
import { WebhookSecretCreatedModal } from '@/lib/crm/components/webhooks/WebhookSecretCreatedModal';
import { logger } from '@/lib/logger';

import { ApiAccessCreateForm } from './api-access-create-form';
import type {
  GrantSelection,
  MintCredentialParams,
} from './api-access-service';
import {
  CREDENTIAL_SLIDEOUT_HEADER,
  CREDENTIAL_SLIDEOUT_WIDTH,
  formatGrantBuildError,
  mintCredential,
  MintCredentialError,
} from './api-access-service';
import { TokenSelfCheckInstruction } from './api-access-token-instruction';

/** DOM id the pinned footer's submit button targets via `form=`. */
const CREATE_FORM_ID = 'api-access-create-form';

interface CreateFooterProps {
  canSubmit: boolean;
  isSubmitting: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Pinned footer: primary action first (matches CreateEntitySlideout), Cancel
 * second. Submitting through `form=` keeps the form the owner of the submit
 * logic while the button stays visible however long the type list gets.
 * Cancel goes through the surface's own dismiss so the unsaved-changes guard
 * still applies; outside any surface it falls back to closing directly.
 */
function CreateFooter({
  canSubmit,
  isSubmitting,
  onOpenChange,
}: CreateFooterProps) {
  const surface = useSurfaceDismiss();
  const handleCancel = useCallback(() => {
    if (surface) {
      surface.close();
      return;
    }
    onOpenChange(false);
  }, [surface, onOpenChange]);

  return (
    <div className='flex items-center gap-3'>
      <Button
        type='submit'
        form={CREATE_FORM_ID}
        color='primary'
        size='md'
        isDisabled={!canSubmit || isSubmitting}
        isLoading={isSubmitting}
      >
        Create service account
      </Button>
      <Button
        color='secondary'
        size='md'
        onClick={handleCancel}
        isDisabled={isSubmitting}
      >
        Cancel
      </Button>
    </div>
  );
}

const HEADER_CONTENT = (
  <>
    <h2 className='text-heading-3 text-[var(--color-text-primary)]'>
      New service account
    </h2>
    <p className='text-sm text-[var(--color-text-tertiary)] mt-1'>
      Name the service account and choose what it can read and write. Its token
      is shown once, right after you create it.
    </p>
  </>
);

export interface ApiAccessCreateSlideoutProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  orgId: number;
  onMinted: () => void;
}

const noop = () => {};

interface MintedSecret {
  data: WebhookSecretCreatedData;
}

export function ApiAccessCreateSlideout({
  isOpen,
  onOpenChange,
  orgId,
  onMinted,
}: ApiAccessCreateSlideoutProps) {
  const [mintedSecret, setMintedSecret] = useState<MintedSecret | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  // Mirrors the form's own submit guard for the footer button, which lives
  // outside the form's subtree.
  const [canSubmit, setCanSubmit] = useState(false);
  // The entity id from a mint attempt that created the principal but then
  // failed (secret attach, or the mutation otherwise erroring afterward).
  // Carried into the next mint call as `existingPrincipalEntityId` so a retry
  // reuses that entity instead of minting a second, orphaned one. Cleared on
  // success and whenever name or grants change — a stale id must never be
  // reused across what is, to the user, a different credential.
  const [retryPrincipalEntityId, setRetryPrincipalEntityId] = useState<
    string | null
  >(null);

  const handleCloseSecretModal = useCallback(() => {
    setMintedSecret(null);
  }, []);

  // Guard against a double-set: keep the first minted secret if one is somehow
  // reported twice before the modal is dismissed.
  const handleSecretMinted = useCallback((next: MintedSecret) => {
    setMintedSecret(prev => prev ?? next);
  }, []);

  const handleFormNameChange = useCallback(() => {
    setRetryPrincipalEntityId(null);
  }, []);

  const handleFormGrantsChange = useCallback((_grants: GrantSelection[]) => {
    setRetryPrincipalEntityId(null);
  }, []);

  // The mint mutation is owned here, in the always-mounted wrapper, rather than
  // in the form body. If the user closes the slideout while a mint is in flight
  // the form unmounts; a mutation owned there would drop its onSuccess/onSettled,
  // losing the one-time secret display and the list refresh. Owning it here keeps
  // both reliable regardless of the form's mount state.
  const mintMutation = useMutation({
    mutationFn: (params: MintCredentialParams) => mintCredential(params),
    onSuccess: res => {
      setRetryPrincipalEntityId(null);
      handleSecretMinted({
        data: {
          bearer_token: res.token,
          secret_hint: res.masked_hint,
          secret_entity_id: res.secretEntityId,
        },
      });
      onOpenChange(false);
    },
    onError: (err, variables) => {
      // Only a MintCredentialError can carry a principal id worth reusing —
      // any other thrown shape means we don't know what happened, so don't guess.
      setRetryPrincipalEntityId(
        err instanceof MintCredentialError
          ? (err.principalEntityId ?? null)
          : null
      );
      const { toastMessage, logPayload } = formatGrantBuildError(
        err,
        'create',
        variables.displayName
      );
      logger.error(
        'Failed to create credential',
        err instanceof Error ? err : new Error(String(err)),
        { ...logPayload, org_id: orgId }
      );
      toast.error(toastMessage);
    },
    // Always refresh the list. A mid-sequence failure (principal created but
    // secret attach failed) leaves an inert partial credential persisted
    // until the retry (see retryPrincipalEntityId) completes it; the list
    // must reflect that partial state in the meantime.
    onSettled: () => {
      onMinted();
    },
  });

  const handleSubmit = useCallback(
    (params: MintCredentialParams) => {
      mintMutation.mutate(
        retryPrincipalEntityId
          ? { ...params, existingPrincipalEntityId: retryPrincipalEntityId }
          : params
      );
    },
    [mintMutation, retryPrincipalEntityId]
  );

  return (
    <>
      <SlideoutMenu
        isOpen={isOpen}
        onOpenChange={onOpenChange}
        header={HEADER_CONTENT}
        headerAlign={CREDENTIAL_SLIDEOUT_HEADER.align}
        headerClassName={CREDENTIAL_SLIDEOUT_HEADER.className}
        footer={
          <CreateFooter
            canSubmit={canSubmit}
            isSubmitting={mintMutation.isPending}
            onOpenChange={onOpenChange}
          />
        }
        hasUnsavedChanges={hasUnsavedChanges}
        onDiscardUnsavedChanges={noop}
        resizable
        initialWidth={CREDENTIAL_SLIDEOUT_WIDTH.initial}
        minWidth={CREDENTIAL_SLIDEOUT_WIDTH.min}
        maxWidth={CREDENTIAL_SLIDEOUT_WIDTH.max}
      >
        {isOpen && (
          <ApiAccessCreateForm
            orgId={orgId}
            formId={CREATE_FORM_ID}
            onSubmit={handleSubmit}
            isSubmitting={mintMutation.isPending}
            onCanSubmitChange={setCanSubmit}
            onDirtyChange={setHasUnsavedChanges}
            onNameChange={handleFormNameChange}
            onGrantsChange={handleFormGrantsChange}
          />
        )}
      </SlideoutMenu>
      <WebhookSecretCreatedModal
        isOpen={!!mintedSecret}
        onClose={handleCloseSecretModal}
        data={mintedSecret?.data ?? null}
        instruction={<TokenSelfCheckInstruction />}
      />
    </>
  );
}
